/**
 * GovBidPacketTab — sections list + editor for the outgoing bid packet.
 * No PDF assembly yet — that's a follow-up.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';

interface Section {
  id: string;
  section_type: string;
  title: string;
  content?: string;
  file_urls?: Array<{ name: string; url: string; mime?: string; size?: number }>;
  status: 'not_started' | 'in_progress' | 'complete';
  order_idx: number;
  created_at: string;
  updated_at: string;
}

const SECTION_TYPES: Array<{ value: string; label: string; icon: string; hint: string }> = [
  { value: 'cover_letter',       label: 'Cover Letter',        icon: '📝', hint: 'Transmittal letter addressed to the CO' },
  { value: 'executive_summary',  label: 'Executive Summary',   icon: '📄', hint: 'High-level summary of the offer' },
  { value: 'technical_approach', label: 'Technical Approach',  icon: '🔧', hint: 'How we will execute the work' },
  { value: 'management_plan',    label: 'Management Plan',     icon: '📋', hint: 'PM structure, communication, QA' },
  { value: 'pricing',            label: 'Pricing / Cost',      icon: '💰', hint: 'Cost breakdown & narrative' },
  { value: 'past_performance',   label: 'Past Performance',    icon: '⭐', hint: 'Reference projects, CPARS ratings' },
  { value: 'key_personnel',      label: 'Key Personnel',       icon: '👥', hint: 'PM, superintendent, safety lead resumes' },
  { value: 'subcontractor_plan', label: 'Subcontractor Plan',  icon: '🤝', hint: 'Sub scopes + subcontracting plan' },
  { value: 'sf1449',             label: 'SF 1449 / Cover Form', icon: '📑', hint: 'Standard form 1449 or SF 33' },
  { value: 'reps_certs',         label: 'Reps & Certs',        icon: '✅', hint: 'Representations & certifications' },
  { value: 'bonding',            label: 'Bonding',             icon: '🔒', hint: 'Bid bond / performance bond docs' },
  { value: 'attachment',         label: 'Attachment',          icon: '📎', hint: 'General attachment' },
  { value: 'signature_page',     label: 'Signature Page',      icon: '✍️', hint: 'Signed offer page' },
  { value: 'custom',             label: 'Custom',              icon: '➕', hint: 'Anything else' },
];

const STATUS_META: Record<string, { label: string; color: string; icon: string }> = {
  not_started: { label: 'Not Started', color: '#64748b', icon: '⬜' },
  in_progress: { label: 'In Progress', color: '#eab308', icon: '🟡' },
  complete:    { label: 'Complete',    color: '#10b981', icon: '✅' },
};

const S: Record<string, React.CSSProperties> = {
  wrap: { display: 'grid', gridTemplateColumns: '300px 1fr', gap: 16 },
  sidebar: {
    background: '#1a2b3c', border: '1px solid #2d4259', borderRadius: 12,
    padding: 12, height: 'fit-content' as const,
  },
  sidebarHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '4px 8px 12px', borderBottom: '1px solid #2d4259', marginBottom: 8,
  },
  sidebarTitle: { fontSize: 13, fontWeight: 800, color: '#fff', margin: 0 },
  addBtn: {
    background: '#4a7eb5', color: '#fff', border: 'none', borderRadius: 6,
    padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
  },
  sectionRow: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 8px',
    borderRadius: 6, cursor: 'pointer', marginBottom: 2,
  },
  sectionRowActive: { background: '#243447' },
  sectionIcon: { fontSize: 16 },
  sectionText: { flex: 1, minWidth: 0 },
  sectionTitle: { fontSize: 13, fontWeight: 600, color: '#e5edf5', whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' },
  sectionMeta: { fontSize: 10, color: '#8fa8c0' },
  emptySide: { color: '#526980', fontSize: 12, padding: 16, textAlign: 'center' as const, fontStyle: 'italic' as const },
  editor: {
    background: '#1a2b3c', border: '1px solid #2d4259', borderRadius: 12,
    padding: 20,
  },
  editorHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 14, gap: 12,
  },
  titleInput: {
    background: 'transparent', border: 'none', color: '#fff', fontSize: 18,
    fontWeight: 800, outline: 'none', flex: 1, padding: 0,
  },
  ta: {
    background: '#243447', border: '1px solid #2d4259', borderRadius: 8,
    padding: 12, color: '#e5edf5', fontSize: 14, width: '100%',
    boxSizing: 'border-box' as const, minHeight: 300,
    resize: 'vertical' as const, fontFamily: 'inherit', lineHeight: 1.5,
  },
  select: {
    background: '#243447', border: '1px solid #2d4259', borderRadius: 6,
    padding: '6px 10px', color: '#e5edf5', fontSize: 12, cursor: 'pointer',
  },
  labelSm: { fontSize: 10, color: '#8fa8c0', textTransform: 'uppercase' as const, letterSpacing: 0.6, marginBottom: 6, fontWeight: 700 },
  fileGrid: { display: 'flex', flexWrap: 'wrap' as const, gap: 8, marginTop: 8 },
  fileChip: {
    background: '#243447', border: '1px solid #2d4259', borderRadius: 6,
    padding: '6px 12px', fontSize: 12, color: '#e5edf5', textDecoration: 'none',
    display: 'inline-flex', alignItems: 'center', gap: 6,
  },
  ghostBtn: {
    background: 'transparent', color: '#8fa8c0', border: '1px solid #2d4259',
    borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer',
  },
  redBtn: {
    background: 'transparent', color: '#ef4444', border: '1px solid #ef4444',
    borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer',
  },
  saveIndicator: { fontSize: 11, color: '#10b981', fontStyle: 'italic' as const },
  emptyEditor: { textAlign: 'center' as const, color: '#526980', padding: 60 },
  addModal: {
    position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20,
  },
  addModalCard: {
    background: '#1a2b3c', border: '1px solid #2d4259', borderRadius: 12,
    padding: 20, width: '100%', maxWidth: 480, maxHeight: '80vh', overflow: 'auto' as const,
  },
  typeGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 },
  typeCard: {
    background: '#243447', border: '1px solid #2d4259', borderRadius: 8,
    padding: 10, cursor: 'pointer', fontSize: 12,
  },
  typeCardHover: { borderColor: '#4a7eb5', background: '#2d4259' },
  progressBar: {
    background: '#243447', borderRadius: 4, height: 6, overflow: 'hidden' as const,
    marginBottom: 12,
  },
  progressFill: { height: '100%', background: '#10b981', transition: 'width 0.3s' },
};

const GovBidPacketTab: React.FC<{ solicitationId: string }> = ({ solicitationId }) => {
  const [sections, setSections] = useState<Section[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [savingIndicator, setSavingIndicator] = useState<null | 'saving' | 'saved'>(null);
  const saveTimer = useRef<any>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/gov/solicitations/${solicitationId}/bid-packet-sections`);
      const list: Section[] = data?.sections || [];
      setSections(list);
      if (list.length > 0 && !activeId) setActiveId(list[0].id);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitationId]);
  useEffect(() => { load(); }, [load]);

  const active = sections.find((s) => s.id === activeId) || null;

  // Debounced autosave when active section content changes
  const updateActiveLocal = (patch: Partial<Section>) => {
    if (!active) return;
    setSections((prev) => prev.map((s) => s.id === active.id ? { ...s, ...patch } : s));
    setSavingIndicator('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.patch(`/gov/bid-packet-sections/${active.id}`, patch);
        setSavingIndicator('saved');
        setTimeout(() => setSavingIndicator(null), 1500);
      } catch (e) {
        setSavingIndicator(null);
        console.error(e);
      }
    }, 700);
  };

  const addSection = async (type: string) => {
    const meta = SECTION_TYPES.find((t) => t.value === type)!;
    try {
      const { data } = await api.post(`/gov/solicitations/${solicitationId}/bid-packet-sections`, {
        section_type: type,
        title: meta.label,
        order_idx: sections.length,
      });
      const s = data.section;
      setSections((prev) => [...prev, s]);
      setActiveId(s.id);
      setShowAdd(false);
    } catch (e: any) { alert(`Failed: ${e?.response?.data?.error || e.message}`); }
  };

  const del = async (s: Section) => {
    if (!window.confirm(`Delete section "${s.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/gov/bid-packet-sections/${s.id}`);
      setSections((prev) => prev.filter((x) => x.id !== s.id));
      if (activeId === s.id) setActiveId(null);
    } catch (e: any) { alert(`Failed: ${e?.response?.data?.error || e.message}`); }
  };

  const move = async (s: Section, dir: -1 | 1) => {
    const idx = sections.findIndex((x) => x.id === s.id);
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= sections.length) return;
    const reordered = [...sections];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    setSections(reordered);
    try {
      await api.post(`/gov/solicitations/${solicitationId}/bid-packet-sections/reorder`, {
        orderedIds: reordered.map((x) => x.id),
      });
    } catch (e) { console.error(e); load(); }
  };

  const completeCount = sections.filter((s) => s.status === 'complete').length;
  const progressPct = sections.length === 0 ? 0 : Math.round((completeCount / sections.length) * 100);

  if (loading) return <div style={{ color: '#8fa8c0', padding: 40, textAlign: 'center' }}>Loading bid packet…</div>;

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
          <div style={{ fontSize: 13, color: '#8fa8c0' }}>
            Bid Packet Progress
          </div>
          <div style={{ fontSize: 13, color: '#e5edf5', fontWeight: 700 }}>
            {completeCount} of {sections.length} sections complete
          </div>
        </div>
        <div style={S.progressBar}>
          <div style={{ ...S.progressFill, width: `${progressPct}%` }} />
        </div>
      </div>

      <div style={S.wrap}>
        {/* Left: section list */}
        <div style={S.sidebar}>
          <div style={S.sidebarHead}>
            <div style={S.sidebarTitle}>SECTIONS</div>
            <button style={S.addBtn} onClick={() => setShowAdd(true)}>+ Add</button>
          </div>
          {sections.length === 0 && (
            <div style={S.emptySide}>No sections yet. Add your first one.</div>
          )}
          {sections.map((s, i) => {
            const meta = SECTION_TYPES.find((t) => t.value === s.section_type) || SECTION_TYPES.find((t) => t.value === 'custom')!;
            const statusMeta = STATUS_META[s.status] || STATUS_META.not_started;
            const isActive = activeId === s.id;
            return (
              <div
                key={s.id}
                style={{ ...S.sectionRow, ...(isActive ? S.sectionRowActive : {}) }}
                onClick={() => setActiveId(s.id)}
              >
                <span style={S.sectionIcon}>{meta.icon}</span>
                <div style={S.sectionText}>
                  <div style={S.sectionTitle}>{s.title}</div>
                  <div style={{ ...S.sectionMeta, color: statusMeta.color }}>
                    {statusMeta.icon} {statusMeta.label}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <button
                    style={{ ...S.ghostBtn, padding: '0 4px', fontSize: 10, opacity: i === 0 ? 0.3 : 1 }}
                    onClick={(e) => { e.stopPropagation(); move(s, -1); }}
                    disabled={i === 0}
                  >▲</button>
                  <button
                    style={{ ...S.ghostBtn, padding: '0 4px', fontSize: 10, opacity: i === sections.length - 1 ? 0.3 : 1 }}
                    onClick={(e) => { e.stopPropagation(); move(s, 1); }}
                    disabled={i === sections.length - 1}
                  >▼</button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Right: editor */}
        {active ? (
          <div style={S.editor}>
            <div style={S.editorHead}>
              <input
                style={S.titleInput}
                value={active.title}
                onChange={(e) => updateActiveLocal({ title: e.target.value })}
              />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {savingIndicator === 'saving' && <span style={{ ...S.saveIndicator, color: '#eab308' }}>Saving…</span>}
                {savingIndicator === 'saved' && <span style={S.saveIndicator}>✓ Saved</span>}
                <select
                  style={S.select}
                  value={active.status}
                  onChange={(e) => updateActiveLocal({ status: e.target.value as any })}
                >
                  <option value="not_started">⬜ Not Started</option>
                  <option value="in_progress">🟡 In Progress</option>
                  <option value="complete">✅ Complete</option>
                </select>
                <button style={S.redBtn} onClick={() => del(active)}>Delete</button>
              </div>
            </div>

            <div style={S.labelSm}>Content</div>
            <textarea
              style={S.ta}
              placeholder={`Write your ${(SECTION_TYPES.find((t) => t.value === active.section_type) || {}).label?.toLowerCase() || 'section'} content here…\n\nFor form-based sections (SF1449, Reps & Certs, Bonding), attach the completed file below.`}
              value={active.content || ''}
              onChange={(e) => updateActiveLocal({ content: e.target.value })}
            />

            <div style={{ marginTop: 20 }}>
              <div style={S.labelSm}>Attachments</div>
              {(!active.file_urls || active.file_urls.length === 0) ? (
                <div style={{ fontSize: 12, color: '#526980', fontStyle: 'italic' }}>
                  No attachments. Files can be uploaded via the Documents tab and referenced here.
                </div>
              ) : (
                <div style={S.fileGrid}>
                  {active.file_urls.map((f, i) => (
                    <a key={i} style={S.fileChip} href={f.url} target="_blank" rel="noreferrer">
                      📎 {f.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div style={{ marginTop: 20, fontSize: 11, color: '#526980', borderTop: '1px solid #2d4259', paddingTop: 12 }}>
              💡 PDF assembly of the full bid packet (combining all sections into one downloadable PDF) coming in a future update.
            </div>
          </div>
        ) : (
          <div style={S.editor}>
            <div style={S.emptyEditor}>
              {sections.length === 0
                ? 'Add your first bid packet section to start building.'
                : 'Select a section from the left to edit it.'}
            </div>
          </div>
        )}
      </div>

      {/* Add Section modal */}
      {showAdd && (
        <div style={S.addModal} onClick={() => setShowAdd(false)}>
          <div style={S.addModalCard} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: 16 }}>Add Bid Packet Section</h3>
              <button style={S.ghostBtn} onClick={() => setShowAdd(false)}>Close</button>
            </div>
            <div style={{ fontSize: 12, color: '#8fa8c0', marginBottom: 4 }}>
              Choose a section type:
            </div>
            <div style={S.typeGrid}>
              {SECTION_TYPES.map((t) => (
                <div key={t.value} style={S.typeCard} onClick={() => addSection(t.value)}
                  onMouseEnter={(e) => Object.assign(e.currentTarget.style, S.typeCardHover)}
                  onMouseLeave={(e) => Object.assign(e.currentTarget.style, S.typeCard)}
                >
                  <div style={{ fontSize: 18, marginBottom: 4 }}>{t.icon}</div>
                  <div style={{ fontWeight: 700, color: '#e5edf5', marginBottom: 2 }}>{t.label}</div>
                  <div style={{ color: '#8fa8c0', fontSize: 11 }}>{t.hint}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GovBidPacketTab;
